import cx from 'classnames'
import * as React from 'react'
import { useCallback, useContext, useState } from 'react'

export interface ToastContent {
  messageType: 'error' | 'success'
  content: React.ReactNode
  title: React.ReactNode
  timeout?: number
}

interface ToastContextValue {
  addToast: (toast: ToastContent) => () => void
}

export const ToastContext = React.createContext<ToastContextValue>({
  addToast: () => () => {
    /* no-op */
  },
})

const Toast = ({ messageType, content, title, onRequestClose }: ToastContent & { onRequestClose: () => void }) => {
  return (
    <article
      className={cx('message', {
        'is-success': messageType === 'success',
        'is-danger': messageType === 'error',
      })}
    >
      <div className="message-header">
        <p>{title}</p>
        <button className="delete" aria-label="delete" onClick={onRequestClose}></button>
      </div>
      <div className="message-body">{content}</div>
    </article>
  )
}

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toastList, setToastList] = useState<(ToastContent & { key: string })[]>([])

  const hideToast = useCallback(
    (key: string) => {
      setToastList((prev) => prev.filter((t) => t.key !== key))
    },
    [setToastList]
  )

  const addToast = useCallback(
    (content: ToastContent) => {
      const item = {
        key: new Date().toISOString(),
        ...content,
      }

      setToastList((prev) => [item, ...prev])

      if (content.timeout) {
        setTimeout(() => hideToast(item.key), content.timeout)
      }

      return () => hideToast(item.key)
    },
    [setToastList, hideToast]
  )

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="toast-list">
        {toastList.map((toast) => {
          return <Toast key={toast.key} onRequestClose={() => hideToast(toast.key)} {...toast} />
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const { addToast } = useContext(ToastContext)

  return {
    addToast,
  }
}
